import { nodeEnv } from 'app.constants';
import { v4 as uuid } from 'uuid';
import { BuilderFeatures } from '../detail/portal-submodule-builder/templates/builder/BuilderFeatures';
import { FlowConfigurationViewModel } from '../detail/portal-submodule-builder/templates/builder/models/viewModels/FlowConfigurationViewModel';
import { ConfigurationFeatures } from '../detail/configurations/ConfigurationFeatures';
export class HostingService {
    static $inject = ['MessagingHubService', 'ConfigurationsService', 'AdminService', 'SubflowService', 'MESSAGINGHUBDOMAIN'];
    constructor(MessagingHubService, ConfigurationsService, AdminService, SubflowService, MESSAGINGHUBDOMAIN) {
        this.MessagingHubService = MessagingHubService;
        this.ConfigurationsService = ConfigurationsService;
        this.AdminService = AdminService;
        this.SubflowService = SubflowService;
        this.MESSAGINGHUBDOMAIN = MESSAGINGHUBDOMAIN;
    }
    async checkConfigurationFeatures() {
        if (this.isConfigurationsApplicationsDomainUsageGetEnabled === undefined) {
            this.isConfigurationsApplicationsDomainUsageGetEnabled = await BuilderFeatures.isUseConfigurationsDomainOnMiscOperationsGetEnabled();
        }
        if (this.isConfigurationsApplicationsDomainUsageSetEnabled === undefined) {
            this.isConfigurationsApplicationsDomainUsageSetEnabled = await BuilderFeatures.isUseConfigurationsDomainOnMiscOperationsSetEnabled();
        }
    }
    /**
     * Retrieve the application configuration for the specified template.
     * @param application
     * @param template
     */
    async getTemplateApplicationJson(application, template) {
        await this.checkConfigurationFeatures();
        return await this.MessagingHubService.sendCommand({
            to: await this.getHostingIdentity(application, undefined, this.isConfigurationsApplicationsDomainUsageSetEnabled, this.isConfigurationsApplicationsDomainUsageGetEnabled),
            method: 'get',
            uri: `/templates/${template}`,
        });
    }
    /**
     * Retrieve the application configuration for the specified template.
     * @param application
     */
    async getConfigurationApplicationJson(application) {
        // Try retrieve the stored configuration, if any.
        await this.checkConfigurationFeatures();
        const configuration = await this.ConfigurationsService.get(await this.getHostingIdentity(application, undefined, this.isConfigurationsApplicationsDomainUsageSetEnabled, this.isConfigurationsApplicationsDomainUsageGetEnabled), undefined, this.isConfigurationsApplicationsDomainUsageGetEnabled);
        return JSON.parse(configuration.Application);
    }
    /**
     * Retrieve the subflow application configuration for the specified template.
     * @param application
     */
    async getSubflowConfigurationApplicationJson(application) {
        const publishedFlow = await this.SubflowService.getPublishedFlowOfSubflow(application.shortName);
        this.checkFeatures();
        return {
            identifier: application.shortName,
            accessKey: this.isSanitizeAccessKeyInBot ? '' : application.accessKey,
            settings: {
                flow: {
                    id: publishedFlow.id ?? uuid(),
                    states: publishedFlow ?? [],
                    configuration: FlowConfigurationViewModel.createDefault(),
                    inputActions: [],
                    outputActions: [],
                },
            },
            settingsType: 'Settings',
        };
    }
    /**
     * Retrieve the stored application.json from hosting configuration.
     * If not available, retrieve the empty template configuration.
     * @param application
     */
    async getApplicationJson(application) {
        try {
            this.checkFeatures();
            if (application.isSubflow) {
                //get published flow
                return await this.getSubflowConfigurationApplicationJson(application);
            }
            else {
                return await this.getConfigurationApplicationJson(application);
            }
        }
        catch (e) {
            const configuration = await this.getTemplateApplicationJson(application, application.isSubflow ? HostingService.SUBFLOW : application.template);
            if (!configuration) {
                return;
            }
            configuration.identifier = application?.shortName;
            if (!this.isSanitizeAccessKeyInBot) {
                configuration.accessKey = application?.accessKey;
            }
            return configuration;
        }
    }
    /**
     * Publishes the template application with the given settings.
     * @param application
     * @param template
     * @param applicationJson
     */
    async publish(application, template, applicationJson, typeOfCompile = undefined) {
        await this.checkConfigurationFeatures();
        const owner = await this.getHostingIdentity(application, typeOfCompile, this.isConfigurationsApplicationsDomainUsageSetEnabled, this.isConfigurationsApplicationsDomainUsageGetEnabled);
        return await this.ConfigurationsService.set(owner, {
            Template: template,
            Application: JSON.stringify(applicationJson),
        }, undefined, this.isConfigurationsApplicationsDomainUsageSetEnabled);
    }
    /**
     * Unpublishes an application with any template.
     * @param application
     */
    async unpublish(application) {
        await this.checkConfigurationFeatures();
        return await this.ConfigurationsService.delete(await this.getHostingIdentity(application, undefined, this.isConfigurationsApplicationsDomainUsageSetEnabled, this.isConfigurationsApplicationsDomainUsageGetEnabled), undefined, this.isConfigurationsApplicationsDomainUsageSetEnabled);
    }
    buildHostingIdentity(prefix) {
        return `${prefix}.${HostingService.HOSTING_IDENTITY_NAME}@${this.MESSAGINGHUBDOMAIN}`;
    }
    async getHostingIdentity(application, typeOfCompile = undefined, useConfigurationsApplicationSet = false, useConfigurationsApplicationGet = false) {
        let identity;
        let adminConfiguration;
        // Try determine the hosting cluster for the application
        const currentClient = this.MessagingHubService.getCurrentClient();
        const currentApplication = currentClient?.application;
        if ((!application.isSubflow)
            && (currentApplication.shortName !== application.shortName)
            && await BuilderFeatures.isIdentityHostingFallback()) {
            await this.MessagingHubService.resetClient(application);
            adminConfiguration = await this.AdminService.get(application, useConfigurationsApplicationGet);
            // we will reset again the client to the original conection
            await this.MessagingHubService.resetClient(currentApplication);
        }
        else {
            //when is a subflow application and the same is accessed with direct link, the current client is undefined
            if (!currentClient ||
                (currentClient.application.shortName !== application.shortName) ||
                (currentClient.application.isSubflow !== application.isSubflow)) {
                await this.MessagingHubService.resetClient(application);
            }
            adminConfiguration = application.isSubflow ? await this.AdminService.get(application.callerApplication, useConfigurationsApplicationGet)
                : await this.AdminService.get(application, useConfigurationsApplicationGet);
        }
        // Custom cluster identity
        if (adminConfiguration && adminConfiguration.Cluster) {
            identity = this.buildHostingIdentity(`${adminConfiguration.Cluster}.${application.template}`);
            // Fallback to standard cluster
        }
        else {
            if (application.isSubflow) {
                identity = this.buildHostingIdentity(application.cluster ? `${application.cluster}.${application.template}` : '' + application.template);
            }
            else {
                identity = this.buildHostingIdentity(application.template);
            }
        }
        identity = identity.toLocaleLowerCase();
        identity = application.template === 'pipeline' ? 'automatic.pipeline.hosting@msging.net' : identity;
        // Local cluster identity, if running
        if (nodeEnv === 'homolog' && await BuilderFeatures.builderLocalDebugHosting()) {
            const localIdentity = this.buildHostingIdentity('local.master.builder');
            const result = await this.MessagingHubService.processCommand({
                to: localIdentity,
                method: 'get',
                uri: '/ping',
            });
            if (result.status === 'success') {
                // Remove current configuration to not collide
                await this.ConfigurationsService.delete(identity, undefined, useConfigurationsApplicationSet);
                identity = localIdentity;
            }
        }
        if (typeOfCompile) {
            identity = `${typeOfCompile}.${identity}`;
        }
        return identity;
    }
    async checkFeatures() {
        if (this.isSanitizeAccessKeyInBot === undefined) {
            this.isSanitizeAccessKeyInBot = await ConfigurationFeatures.isSanitizeAccessKeyInBot();
        }
    }
}
HostingService.HOSTING_IDENTITY_NAME = 'hosting';
HostingService.ADMIN_IDENTITY_NAME = 'admin';
HostingService.SUBFLOW = 'subflow';
HostingService.$inject = ['MessagingHubService', 'ConfigurationsService', 'AdminService', 'SubflowService', 'MESSAGINGHUBDOMAIN'];
